import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import ScopeClauseCard from "./ScopeClauseCard";
import FormButton from "../form-components/FormButton";

/**
 * ScopeGroupCard — card representing a single AND-group of scope clauses.
 *
 * Header:  "Included if all of the following match" + optional remove-group icon
 * Body:    Stacked ScopeClauseCard components
 * Footer:  "Add condition" button
 */
const ScopeGroupCard = ({
  group,
  groupIndex,
  onUpdateClause,
  onAddClause,
  onRemoveClause,
  onRemoveGroup,
  showRemoveGroup,
  dimensions,
  activeClauseId,
  setActiveClauseId,
}) => {
  return (
    <div className="border border-card-bd rounded-card bg-card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-text-2 uppercase tracking-wide">
          Included if all of the following match
        </p>
        {showRemoveGroup && (
          <button
            type="button"
            onClick={onRemoveGroup}
            className="p-1 text-text-2 hover:text-danger transition-colors cursor-pointer"
            title="Remove group"
            aria-label="Remove group"
          >
            <FontAwesomeIcon
              icon={icon({ name: "trash", style: "solid" })}
              className="text-xs"
            />
          </button>
        )}
      </div>

      {/* Clause list */}
      <div className="space-y-3">
        {group.clauses.map((clause) => (
          <ScopeClauseCard
            key={clause.id}
            clause={clause}
            clauseId={clause.id}
            dimensions={dimensions}
            isExpanded={activeClauseId === clause.id}
            onToggle={(cId) =>
              setActiveClauseId((prev) => (prev === cId ? null : cId))
            }
            onChange={(cId, updated) => onUpdateClause(cId, updated)}
            onRemove={(cId) => onRemoveClause(cId)}
          />
        ))}
      </div>

      {/* Footer — add condition */}
      <FormButton
        title="Add condition"
        style="tertiary"
        onClick={onAddClause}
        icon={<FontAwesomeIcon icon={icon({ name: "plus", style: "solid" })} />}
        className="mt-3 w-full"
      />
    </div>
  );
};

ScopeGroupCard.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    clauses: PropTypes.array.isRequired,
  }).isRequired,
  groupIndex: PropTypes.number.isRequired,
  onUpdateClause: PropTypes.func.isRequired,
  onAddClause: PropTypes.func.isRequired,
  onRemoveClause: PropTypes.func.isRequired,
  onRemoveGroup: PropTypes.func.isRequired,
  showRemoveGroup: PropTypes.bool,
  dimensions: PropTypes.array,
  activeClauseId: PropTypes.string,
  setActiveClauseId: PropTypes.func.isRequired,
};

export default ScopeGroupCard;
